import { useMemo, useRef, useState } from "react";
import { Search, SquareCheck, Users, FileText, TriangleAlert } from "lucide-react";
import { Input } from "@/components/ui/input";

const PARAMETERS = [
  {
    key: "chasing_progress",
    name: "Chasing Progress",
    mobileName: ["Chasing Progress"],
    icon: Search,
    color: "#ff9f43",
    tint: "#fff3e2",
  },
  {
    key: "follow_up",
    name: "Follow Up on Commitments",
    mobileName: ["Follow Up on Commitments"],
    icon: SquareCheck,
    color: "#ff6b6b",
    tint: "#ffe9e9",
  },
  {
    key: "coordinating",
    name: "Coordinating Across Teams",
    mobileName: ["Coordinating Across Teams"],
    icon: Users,
    color: "#2fbf71",
    tint: "#e4f7ec",
  },
  {
    key: "decisions",
    name: "Managing Decisions & Approvals",
    mobileName: ["Managing Decisions", "& Approvals"],
    icon: FileText,
    color: "#4a90e2",
    tint: "#e8f1fc",
  },
  {
    key: "exceptions",
    name: "Handling Exceptions & Escalations",
    mobileName: ["Handling Exceptions", "& Escalations"],
    icon: TriangleAlert,
    color: "#a855f7",
    tint: "#f3e9fd",
  },
];

const ACTUAL_COLOR = "#d5dce4";
const ACTUAL_TEXT = "#64748b";

// fixed defaults (deterministic so statically prerendered HTML matches the client)
const DEFAULT_VALUES = {
  chasing_progress: 6,
  follow_up: 6.5,
  coordinating: 7,
  decisions: 3,
  exceptions: 3,
};

export default function Calculator({ embedded = false }) {
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [baseline, setBaseline] = useState(45);
  const barRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [hasDragged, setHasDragged] = useState(false);

  const overhead = useMemo(
    () => Object.values(values).reduce((a, b) => a + b, 0),
    [values]
  );
  const actual = Math.max(0, baseline - overhead);
  const overheadPct = baseline > 0 ? Math.round((overhead / baseline) * 100) : 0;
  const maxPerParam = baseline / 5;

  // drag a segment divider: moving it resizes the parameter above it,
  // core work absorbs the difference
  const setParam = (key, v) => {
    setValues((prev) => {
      const others = Object.entries(prev)
        .filter(([k]) => k !== key)
        .reduce((a, [, val]) => a + val, 0);
      const clamped = Math.max(0, Math.min(maxPerParam, baseline - others, v));
      return { ...prev, [key]: Math.round(clamped * 2) / 2 };
    });
  };

  const startDrag = (e, key) => {
    if (!barRef.current || baseline <= 0) return;
    e.preventDefault();
    dragRef.current = {
      key,
      startY: e.clientY,
      startVal: values[key],
      barH: barRef.current.getBoundingClientRect().height,
    };
    setDragging(key);
    setHasDragged(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const moveDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const deltaHrs = ((e.clientY - d.startY) / d.barH) * baseline;
    setParam(d.key, d.startVal + deltaHrs);
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragging(null);
  };

  const handleBaseline = (e) => {
    const n = parseInt(e.target.value, 10);
    if (Number.isNaN(n)) return setBaseline(0);
    setBaseline(Math.min(100, Math.max(0, n)));
  };

  // segment heights as % of bar
  const segments = [
    ...PARAMETERS.map((p) => ({
      key: p.key,
      value: values[p.key],
      color: p.color,
      text: "#ffffff",
    })),
    { key: "actual", value: actual, color: ACTUAL_COLOR, text: ACTUAL_TEXT },
  ].filter((s) => s.value > 0);

  return (
    <div>
      <div
        className={`mx-auto ${
          embedded ? "max-w-none pb-6" : "max-w-[760px] px-6 md:px-12 pt-36 pb-24 md:pt-44"
        }`}
      >
        <div
          data-testid="time-calculator"
          className="reveal relative border border-[#e8e8e6] rounded-[20px] px-4 py-6 md:p-8 bg-white overflow-hidden"
          style={{ animationDelay: "0.1s" }}
        >
          {/* work week control — top right corner */}
          <div className="absolute top-5 right-6 z-10 flex items-center gap-2">
            <span className="font-mono-x text-[10px] uppercase tracking-[1.5px] text-[#9c9c9c]">
              Work week
            </span>
            <Input
              data-testid="baseline-input"
              aria-label="Work week hours"
              type="number"
              min={0}
              max={100}
              value={baseline}
              onChange={handleBaseline}
              className="w-14 h-7 px-2 text-center font-mono-x text-xs bg-white border-[#e8e8e6] rounded-full focus-visible:ring-1 focus-visible:ring-[#131313]"
            />
            <span className="font-mono-x text-[10px] uppercase tracking-[1.5px] text-[#9c9c9c]">
              hrs
            </span>
          </div>

          <div className="flex flex-row items-stretch gap-4 md:gap-10 pt-12 md:pt-8">
            {/* stacked bar */}
            <div className="relative flex items-stretch shrink-0">
              {/* bar */}
              <div ref={barRef} className="relative w-[64px] md:w-[96px] h-[380px] md:h-[460px]">
                <div
                  data-testid="stacked-bar"
                  className="bar-grow absolute inset-0 rounded-[16px] overflow-hidden flex flex-col shadow-[0_18px_40px_-18px_rgba(19,19,19,0.25)]"
                >
                  {segments.map((s) => {
                    const pct = baseline > 0 ? (s.value / baseline) * 100 : 0;
                    return (
                      <div
                        key={s.key}
                        data-testid={`segment-${s.key}`}
                        className={`flex items-center justify-center ${dragging ? "" : "transition-all duration-300"}`}
                        style={{ height: `${pct}%`, backgroundColor: s.color }}
                      >
                        {pct >= 3 && (
                          <span
                            className="font-mono-x text-[12px] font-semibold tabular-nums"
                            style={{ color: s.text }}
                          >
                            {s.value}h
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* drag handles — one at the bottom edge of each parameter segment */}
                {(() => {
                  let cum = 0;
                  return PARAMETERS.map((p) => {
                    cum += values[p.key];
                    const topPct = baseline > 0 ? (cum / baseline) * 100 : 0;
                    const active = dragging === p.key;
                    return (
                      <div
                        key={p.key}
                        data-testid={`divider-${p.key}`}
                        role="slider"
                        aria-label={`${p.name} hours`}
                        aria-valuenow={values[p.key]}
                        aria-valuemin={0}
                        aria-valuemax={maxPerParam}
                        tabIndex={0}
                        onPointerDown={(e) => startDrag(e, p.key)}
                        onPointerMove={moveDrag}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        onKeyDown={(e) => {
                          if (["ArrowUp", "ArrowRight"].includes(e.key)) {
                            e.preventDefault();
                            setHasDragged(true);
                            setParam(p.key, values[p.key] + 0.5);
                          } else if (["ArrowDown", "ArrowLeft"].includes(e.key)) {
                            e.preventDefault();
                            setHasDragged(true);
                            setParam(p.key, values[p.key] - 0.5);
                          }
                        }}
                        className="absolute left-0 right-0 h-[22px] -translate-y-1/2 cursor-ns-resize touch-none group focus-visible:outline-none"
                        style={{ top: `${topPct}%`, zIndex: active ? 20 : 10 }}
                      >
                        <div
                          className={`absolute left-[3px] right-[3px] top-1/2 -translate-y-1/2 h-[3px] rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-colors ${
                            active ? "bg-white" : "bg-white/80 group-hover:bg-white group-focus-visible:bg-white"
                          }`}
                        />
                        <div
                          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-[3px] rounded-full bg-white px-[7px] py-[4px] shadow-md transition-opacity ${
                            active || (p.key === "coordinating" && !hasDragged)
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
                          }`}
                        >
                          <span className="block w-[3px] h-[3px] rounded-full bg-[#9c9c9c]" />
                          <span className="block w-[3px] h-[3px] rounded-full bg-[#9c9c9c]" />
                          <span className="block w-[3px] h-[3px] rounded-full bg-[#9c9c9c]" />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* parameter rows */}
            <div className="flex-1 min-w-0 flex flex-col justify-start gap-5 md:gap-7 md:max-w-[420px] pt-1">
              {PARAMETERS.map((p) => {
                const Icon = p.icon;
                const active = dragging === p.key;
                return (
                  <div key={p.key} data-testid={`row-${p.key}`} className="flex items-center gap-2 md:gap-3">
                    <div
                      className="w-7 h-7 md:w-9 md:h-9 rounded-[9px] md:rounded-[12px] flex items-center justify-center shrink-0 transition-shadow"
                      style={{ backgroundColor: p.tint, boxShadow: active ? `0 0 0 2px ${p.color}` : "none" }}
                    >
                      <Icon size={14} strokeWidth={2.2} style={{ color: p.color }} className="md:hidden" />
                      <Icon size={17} strokeWidth={2.2} style={{ color: p.color }} className="hidden md:block" />
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-x-2">
                      <div
                        className="text-[13px] md:text-[15px] font-medium leading-snug flex-1 min-w-0 transition-colors"
                        style={{ color: active ? p.color : "#131313" }}
                      >
                        <span className="md:hidden">
                          {p.mobileName[0]}
                          {p.mobileName[1] && (
                            <>
                              <br />
                              {p.mobileName[1]}
                            </>
                          )}
                        </span>
                        <span className="hidden md:inline">{p.name}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* overhead highlight — sits below the last parameter row */}
              <div className="mt-4 md:mt-5 pt-5 md:pt-6 border-t border-[#e8e8e6] text-center">
                <span
                  data-testid="overhead-summary"
                  className="relative inline-block font-display text-[#131313] tabular-nums"
                  style={{ fontSize: "clamp(44px, 7vw, 72px)", lineHeight: 1, letterSpacing: "-2px" }}
                >
                  {overheadPct}%
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    className="absolute -right-6 md:-right-8 -top-1.5 md:-top-2 w-[20px] h-[20px] md:w-[26px] md:h-[26px]"
                  >
                    <g stroke="#ff9f43" strokeWidth="2.4" strokeLinecap="round" fill="none">
                      <line x1="5" y1="18.5" x2="8.1" y2="6.9" />
                      <line x1="5.5" y1="17.5" x2="12.4" y2="7.7" />
                      <line x1="6.5" y1="17" x2="16.3" y2="10.1" />
                    </g>
                  </svg>
                </span>
                <div className="font-display text-[#131313] text-[14px] md:text-lg mt-2 md:mt-3">
                  of your workweek goes into coordination.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
